/**
 * Which x-axis labels to print so they do not collide.
 *
 * The time series prints one label per bucket, which fits eight `2026-01`
 * strings into a 1088px card and the same eight into 332px on a phone. At
 * 390px the measured labels are ~41px wide with ~46px of pitch, so
 * neighbours overlapped and `2026-01` / `2026-02` read as `2026-2026-02`.
 *
 * Rather than hard-code "every other at narrow widths", derive the stride
 * from the space actually available: how many labels fit, given a width and a
 * per-label estimate. That keeps a wide card fully labelled, thins a phone to
 * alternate buckets, and still degrades correctly if a series grows to
 * hundreds of points.
 *
 * The first and last buckets always print. They are the ones that say what
 * range the chart covers, and dropping either makes the axis unreadable.
 */

/** Rough px per label. `2026-01` at 10px tabular digits measures ~41px. */
export const LABEL_WIDTH_ESTIMATE = 46;

export function labelStride(
  count: number,
  width: number,
  labelWidth: number = LABEL_WIDTH_ESTIMATE,
): number {
  if (count <= 2) return 1;
  // Unmeasured (first paint, or jsdom): label everything rather than thin on
  // a guess. A one-frame collision beats a permanently sparse axis if the
  // measurement never arrives.
  if (width <= 0) return 1;

  const fits = Math.floor(width / labelWidth);
  if (fits >= count) return 1;
  // At least two labels, otherwise stride swallows the whole series.
  return Math.max(1, Math.ceil((count - 1) / Math.max(fits - 1, 1)));
}

/**
 * True when this index should print its label.
 *
 * Anchored from the END, not the start. The last bucket says where the series
 * finishes, so it always prints, and striding backwards from it keeps the
 * right edge clean. The first bucket always prints too.
 *
 * The subtle case is an even count: striding back from the last index lands on
 * index 1, which then sits adjacent to the always-printed index 0 and
 * re-creates the collision at the left edge instead of the right. So a
 * strided pick that is closer than `stride` to the first index is dropped,
 * and index 0 keeps its label.
 */
export function showsLabel(
  index: number,
  count: number,
  stride: number,
): boolean {
  if (stride <= 1) return true;
  if (index === 0 || index === count - 1) return true;
  if ((count - 1 - index) % stride !== 0) return false;
  // Too close to the first label to stand on its own.
  return index >= stride;
}
